import { openKvStore } from '../offline/idb.js';
import { MediaCache } from '../offline/media-cache.js';
const FILE_SLOT = 'file';
/**
 * Wraps any {@link ReaderSource}, persisting reading progress locally so a
 * checkpoint survives reloads and works offline. Manifest/page/file calls pass
 * straight through. See docs/image-engine-spec.md §2.2.1.
 *
 * Progress writes are mirrored to the wrapped source (best-effort). If that
 * write fails (offline), it is queued and flushed on the next `online` event.
 */
export class CachedSource {
    #inner;
    #store;
    #ns;
    #media;
    #queue = [];
    #flushing = false;
    #onOnline = () => void this.flush();
    constructor(inner, opts = {}) {
        this.#inner = inner;
        this.#store = opts.store ?? openKvStore();
        this.#ns = opts.namespace ?? 'pore';
        this.#media =
            opts.cache === false
                ? null
                : new MediaCache(typeof opts.cache === 'object' ? opts.cache : {});
        if (typeof addEventListener === 'function')
            addEventListener('online', this.#onOnline);
    }
    /** Live manifest when online; the copy saved by {@link download} when the wrapped source is unreachable (offline). */
    async getManifest(bookId) {
        try {
            const manifest = await this.#inner.getManifest(bookId);
            await this.#store.set(this.#manifestKey(bookId), manifest).catch(() => { });
            return manifest;
        }
        catch (err) {
            const cached = await this.#store
                .get(this.#manifestKey(bookId))
                .catch(() => null);
            if (cached)
                return cached;
            throw err;
        }
    }
    /** Cached blob first (once downloaded), then the wrapped source. */
    async getPage(bookId, index, opts) {
        const hit = await this.#media?.getBlob(bookId, String(index));
        if (hit)
            return hit;
        return this.#inner.getPage(bookId, index, opts);
    }
    async getFile(bookId, opts) {
        const hit = await this.#media?.getBlob(bookId, FILE_SLOT);
        if (hit)
            return hit;
        return this.#inner.getFile(bookId, opts);
    }
    /**
     * Pull a whole book into the offline cache — every page for an image book,
     * the file for EPUB / PDF. Safe to call again to resume a partial download.
     */
    async download(bookId, opts = {}) {
        if (!this.#media)
            throw new Error('CachedSource: media cache is disabled');
        const manifest = await this.getManifest(bookId); // also persists it for offline getManifest()
        if (manifest.type === 'image') {
            const total = manifest.pageCount;
            for (let i = 0; i < total; i++) {
                opts.signal?.throwIfAborted();
                if (!(await this.#media.getBlob(bookId, String(i)))) {
                    const page = await this.#inner.getPage(bookId, i, sig(opts.signal));
                    const blob = typeof page === 'string' ? await (await fetch(page)).blob() : page;
                    await this.#media.putBlob(bookId, String(i), blob, total);
                }
                opts.onProgress?.(await this.downloadStatus(bookId));
            }
            return;
        }
        opts.signal?.throwIfAborted();
        if (!(await this.#media.getBlob(bookId, FILE_SLOT))) {
            const file = await this.#inner.getFile(bookId, sig(opts.signal));
            await this.#media.putBlob(bookId, FILE_SLOT, file, 1);
        }
        opts.onProgress?.(await this.downloadStatus(bookId));
    }
    async downloadStatus(bookId) {
        const meta = await this.#media?.meta(bookId);
        if (!meta || meta.slots.length === 0) {
            return { state: 'none', cached: 0, total: 0, bytes: 0 };
        }
        const hasFile = meta.slots.includes(FILE_SLOT);
        const total = hasFile ? 1 : (meta.pageCount ?? meta.slots.length);
        const cached = hasFile ? 1 : meta.slots.filter((s) => s !== FILE_SLOT).length;
        return {
            state: cached >= total ? 'complete' : 'partial',
            cached,
            total,
            bytes: meta.bytes,
        };
    }
    /** Alias kept short for the common check. */
    async downloadState(bookId) {
        return (await this.downloadStatus(bookId)).state;
    }
    removeDownload(bookId) {
        return this.#media?.removeBook(bookId) ?? Promise.resolve();
    }
    /** Local copy wins; falls back to the wrapped source if we have nothing. */
    async loadProgress(bookId) {
        const local = await this.#store.get(this.#key(bookId));
        if (local)
            return local;
        try {
            return await this.#inner.loadProgress(bookId);
        }
        catch {
            return null;
        }
    }
    async saveProgress(bookId, p) {
        await this.#store.set(this.#key(bookId), p);
        try {
            await this.#inner.saveProgress(bookId, p);
        }
        catch {
            this.#enqueue({ bookId, position: p, at: Date.now() });
        }
    }
    /** Local copy wins; falls back to the wrapped source when we have nothing (both optional — most sources have neither). */
    async loadHighlights(bookId) {
        const local = await this.#store.get(this.#hlKey(bookId));
        if (local)
            return local;
        try {
            return (await this.#inner.loadHighlights?.(bookId)) ?? [];
        }
        catch {
            return [];
        }
    }
    /** Mirrors {@link saveProgress}'s local-first approach, without the offline queue (a highlight save can just retry on the next call). */
    async saveHighlights(bookId, highlights) {
        await this.#store.set(this.#hlKey(bookId), highlights);
        try {
            await this.#inner.saveHighlights?.(bookId, highlights);
        }
        catch {
            // best-effort mirror; the local copy above is authoritative for loadHighlights
        }
    }
    /** Bookmarks — same local-first pattern as highlights. */
    async loadBookmarks(bookId) {
        const local = await this.#store.get(this.#bmKey(bookId));
        if (local)
            return local;
        try {
            return (await this.#inner.loadBookmarks?.(bookId)) ?? [];
        }
        catch {
            return [];
        }
    }
    async saveBookmarks(bookId, bookmarks) {
        await this.#store.set(this.#bmKey(bookId), bookmarks);
        try {
            await this.#inner.saveBookmarks?.(bookId, bookmarks);
        }
        catch {
            // best-effort mirror
        }
    }
    /** Push any queued offline writes to the wrapped source. */
    async flush() {
        if (this.#flushing || this.#queue.length === 0)
            return;
        this.#flushing = true;
        // keep only the latest write per book
        const latest = new Map();
        for (const w of this.#queue)
            latest.set(w.bookId, w);
        this.#queue = [];
        try {
            for (const w of latest.values()) {
                try {
                    await this.#inner.saveProgress(w.bookId, w.position);
                }
                catch {
                    this.#queue.push(w);
                }
            }
        }
        finally {
            this.#flushing = false;
        }
    }
    get pendingWrites() {
        return this.#queue.length;
    }
    dispose() {
        if (typeof removeEventListener === 'function')
            removeEventListener('online', this.#onOnline);
    }
    #enqueue(w) {
        this.#queue = this.#queue.filter((q) => q.bookId !== w.bookId);
        this.#queue.push(w);
    }
    #key(bookId) {
        return `${this.#ns}:progress:${bookId}`;
    }
    #hlKey(bookId) {
        return `${this.#ns}:highlights:${bookId}`;
    }
    #bmKey(bookId) {
        return `${this.#ns}:bookmarks:${bookId}`;
    }
    #manifestKey(bookId) {
        return `${this.#ns}:manifest:${bookId}`;
    }
}
function sig(signal) {
    return signal ? { signal } : undefined;
}
//# sourceMappingURL=cached-source.js.map